/**
 * Restricted apps (BARN-345, contract C1 v3).
 *
 * A Seed version may mark an app `restricted: true` in
 * `seed_versions.meta.appsConfig[key]` (today only the harness-injected
 * `portal-gfnet`). Two rules follow, everywhere an app list is read:
 *
 *   1. A workspace app list `null` ("all apps") means every app of the served
 *      version EXCEPT the restricted ones.
 *   2. A restricted app reaches a person only when a workspace they belong to
 *      names its key. Console admins included; a magic link never does.
 *
 * Dispatch enforces rule 2 by path: a restricted app owns its `path` (or
 * `/<key>`) plus the extra `restrictedPaths` prefixes it declares (its API),
 * and the harness's connect-as route for it (`/api/eam/v1/connect-as/<key>`).
 *
 * For a version with no restricted app every helper here is the identity of
 * the rule that existed before, which is what keeps those shares unchanged.
 *
 * Pure on purpose: no imports, no D1 types, so the container portal can use it.
 * Used by: dispatch worker, console worker + portal, container worker + portal.
 */

/** A restricted app and the path prefixes it owns (lower-case, no trailing `/`). */
export interface RestrictedApp {
  key: string
  prefixes: string[]
}

/**
 * What a person's workspaces grant: `all` = some workspace has the `null`
 * ("all apps") list, which covers every NON-restricted app; `named` = every key
 * a workspace lists by name. Only `named` can grant a restricted app.
 */
export type AppAccess = { all: boolean; named: string[] }

type AppConfigEntry = { path?: unknown; restricted?: unknown; restrictedPaths?: unknown }

function configEntries(appsConfig: unknown): Array<[string, AppConfigEntry]> {
  if (!appsConfig || typeof appsConfig !== 'object' || Array.isArray(appsConfig)) return []
  return Object.entries(appsConfig as Record<string, unknown>)
    .filter((e): e is [string, AppConfigEntry] => !!e[1] && typeof e[1] === 'object')
}

/** `'/pwa/'` and `'/pwa'` are the same prefix; a root path is `''` (matches everything). */
function stripTrailing(prefix: string): string {
  return prefix.replace(/\/+$/, '').toLowerCase()
}

/** A `restrictedPaths` entry is valid when it starts and ends with `/` and is longer than `/`. */
function validExtraPrefix(p: unknown): p is string {
  return typeof p === 'string' && p.length > 1 && p.startsWith('/') && p.endsWith('/')
}

/**
 * The restricted apps of a version's `appsConfig`. Only a literal
 * `restricted === true` counts; `restrictedPaths` without it is ignored, and
 * malformed entries in it are dropped.
 */
export function restrictedAppsOf(appsConfig: unknown): RestrictedApp[] {
  const out: RestrictedApp[] = []
  for (const [key, value] of configEntries(appsConfig)) {
    if (value.restricted !== true) continue
    const own = typeof value.path === 'string' && value.path.startsWith('/') ? value.path : `/${key}`
    const prefixes = [stripTrailing(own)]
    if (Array.isArray(value.restrictedPaths)) {
      for (const p of value.restrictedPaths) {
        if (!validExtraPrefix(p)) continue
        // Normalised like a request path, so `//` or `/./` cannot become the root.
        const norm = normaliseRequestPath(p)
        const prefix = norm === null ? '' : stripTrailing(norm)
        if (prefix !== '') prefixes.push(prefix)
      }
    }
    out.push({ key, prefixes: [...new Set(prefixes)] })
  }
  return out
}

export function hasRestrictedApps(appsConfig: unknown): boolean {
  return restrictedAppsOf(appsConfig).length > 0
}

/** The keys of a version's restricted apps. */
export function restrictedKeysOf(appsConfig: unknown): string[] {
  return restrictedAppsOf(appsConfig).map(a => a.key)
}

/**
 * Combine workspace app lists into one access. No list at all is "all apps",
 * as an empty row set has always been; any `null` list sets `all`; `named` is
 * the sorted union of the explicit lists either way.
 */
export function accessFromLists(lists: Array<string[] | null>): AppAccess {
  if (lists.length === 0) return { all: true, named: [] }
  const named = new Set<string>()
  let all = false
  for (const l of lists) {
    if (l === null) { all = true; continue }
    for (const a of l) if (typeof a === 'string') named.add(a)
  }
  return { all, named: [...named].sort() }
}

/** Whether an access reaches one app. A restricted app needs its key named. */
export function appAllowed(access: AppAccess, key: string, restricted: boolean): boolean {
  if (access.named.includes(key)) return true
  return !restricted && access.all
}

/**
 * Expand a workspace app list against the served version: `null` becomes the
 * version's keys minus its restricted apps; an explicit list is returned as is.
 */
export function expandAppList(
  list: string[] | null,
  versionKeys: string[],
  appsConfig: unknown,
): string[] {
  if (list !== null) return list
  const restricted = new Set(restrictedKeysOf(appsConfig))
  return versionKeys.filter(k => !restricted.has(k))
}

/**
 * A request path as a seed router may end up reading it: percent-decoded,
 * `\` read as `/`, `//` collapsed, `.`/`..` segments resolved, lower-cased.
 * `null` when it cannot be decoded (callers treat that as "could be anything").
 */
export function normaliseRequestPath(pathname: string): string | null {
  let decoded: string
  try {
    decoded = decodeURIComponent(pathname)
  } catch {
    return null
  }
  const segments: string[] = []
  for (const seg of decoded.replace(/\\/g, '/').split('/')) {
    if (seg === '' || seg === '.') continue
    if (seg === '..') { segments.pop(); continue }
    segments.push(seg)
  }
  const trailing = /[/\\]$/.test(decoded) && segments.length > 0 ? '/' : ''
  return ('/' + segments.join('/') + trailing).toLowerCase()
}

function underPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(prefix + '/')
}

/**
 * The harness's connect-as route (eam-core `CONNECT_AS_PREFIX`): `GET
 * /api/eam/v1/connect-as/<app>` lists the people that app admits, so it belongs
 * to `<app>` as much as the app's own paths do.
 */
const CONNECT_AS_PREFIX = '/api/eam/v1/connect-as/'

/**
 * EVERY restricted app whose prefix the path is equal to or under, or whose
 * connect-as route it is, in declaration order (prefixes may nest or coincide,
 * and each needs its own grant). An undecodable path, with restricted apps
 * present, is under all of them.
 */
export function restrictedAppsForPath(pathname: string, restricted: RestrictedApp[]): string[] {
  if (restricted.length === 0) return []
  const path = normaliseRequestPath(pathname)
  if (path === null) return restricted.map(a => a.key)
  const connectAs = path.startsWith(CONNECT_AS_PREFIX) ? path.slice(CONNECT_AS_PREFIX.length).replace(/\/$/, '') : null
  return restricted
    .filter(a => connectAs === a.key.toLowerCase() || a.prefixes.some(p => underPrefix(path, p)))
    .map(a => a.key)
}

/**
 * The first restricted app covering this path that the access does not grant,
 * or `null` when the request may go on. A magic link grants no restricted app.
 */
export function deniedRestrictedApp(
  pathname: string,
  restricted: RestrictedApp[],
  access: AppAccess,
  opts: { magicLink?: boolean } = {},
): string | null {
  for (const key of restrictedAppsForPath(pathname, restricted)) {
    if (opts.magicLink || !appAllowed(access, key, true)) return key
  }
  return null
}
